import {
  ALL_TAB_COURSE_CARDS_LENGTH,
  ALL_TAB_PROGRAM_CARDS_LENGTH,
  ALL_TAB_2U_CARDS_LENGTH,
  FIRST_LEVEL_RESULTS,
  RESULT_TYPE_NONE,
  RESULT_TYPE_BOOT_CAMP,
  RESULT_TYPE_COURSE,
  RESULT_TYPE_DEGREE_PROGRAM,
  RESULT_TYPE_EXECUTIVE_EDUCATION,
  RESULT_TYPE_PROGRAM,
  SECOND_LEVEL_RESULTS,
} from './search';

/**
 * Tracks an event from components through our react-tracking approach before
 * sending all data to segment.
 * @param tracking the tracking prop passed through react-tracking
 * @param componentProps the component level analytics data being sent
 * @param eventName name of the event being sent
 */
const submitEvent = (tracking, componentProps, eventName) => {
  tracking.trackEvent(componentProps);

  const { dataLayer = [] } = window;

  // If top level tracking is not present just send component level data
  const allProps = dataLayer.slice(-1)[0] || componentProps;

  window.analytics.track(eventName, allProps);
};

const addTrackingToLink = (tracking, eventName, ref, propsFunction) => {
  if (!ref || ref.hasAttribute('__tracked')) {
    return;
  }
  const wrappedPropsFunction = () => {
    tracking.trackEvent(propsFunction());
    return propsFunction();
  };
  window.analytics.trackLink(ref, eventName, wrappedPropsFunction);
  ref.setAttribute('__tracked', true);
};

const submitPageViewAnalytics = () => window.analytics.page();

/**
 * Tracks an event if Segment is present and available to send the event.
 * @param eventName name of the event being sent
 * @param eventProps properties that we want to send along with the event
 */
const emitEventIfAble = (eventName, eventProps) => {
  if (typeof window !== 'undefined') {
    if (window.analytics && typeof window.analytics.track === 'function') {
      window.analytics.track(eventName, eventProps);
    }
  }
};

const trackSearchResults = (reduxResults, query, refinements, pageType, resultType) => {
  const hasFilters = JSON.stringify(refinements) !== '{}';
  const courses = reduxResults.courses || { items: [], currentPage: 1 };
  const programs = reduxResults.programs || { items: [], currentPage: 1 };
  const execEd = reduxResults[RESULT_TYPE_EXECUTIVE_EDUCATION] || { items: [] };
  const degree = reduxResults[RESULT_TYPE_DEGREE_PROGRAM] || { items: [] };
  const bootcamp = reduxResults[RESULT_TYPE_BOOT_CAMP] || { items: [] };

  const firstLevelResultsItems = courses.items.slice(0, ALL_TAB_COURSE_CARDS_LENGTH)
    .concat(programs.items.slice(0, ALL_TAB_PROGRAM_CARDS_LENGTH))
    .concat(execEd.items.slice(0, ALL_TAB_2U_CARDS_LENGTH))
    .concat(degree.items.slice(0, ALL_TAB_2U_CARDS_LENGTH))
    .concat(bootcamp.items.slice(0, ALL_TAB_2U_CARDS_LENGTH));

  const displayedUuids = resultType === RESULT_TYPE_NONE
    ? firstLevelResultsItems.map(item => item.uuid)
    : reduxResults[resultType].items
      .filter(item => Object.prototype.hasOwnProperty.call(item, 'uuid'))
      .map(item => item.uuid);

  const currentPage = resultType !== RESULT_TYPE_NONE
    ? reduxResults[resultType].currentPage + 1
    : null;

  // Determine which sections of results are displayed.  This will be one or more of
  // the course, program, executive education, boot camp, or degree program
  // sections. The display of a section is not necessarily connected to
  // the presence of results for that type.
  //
  // On first level result pages:
  // - Any result section *could* be displayed.
  // - edX program and course sections are always displayed on first level result pages even if
  //   the display is a message that there are no results.
  // - 2U sections are only displayed if there are results and no filters applied.
  //
  // On second level result pages:
  // - The section is displayed only if it matches the current result type.
  const isDisplayed = {
    courses: pageType === FIRST_LEVEL_RESULTS || resultType === RESULT_TYPE_COURSE,
    programs: pageType === FIRST_LEVEL_RESULTS || resultType === RESULT_TYPE_PROGRAM,
    execEd:
      (pageType === FIRST_LEVEL_RESULTS && !hasFilters && execEd.items.length > 0)
      || resultType === RESULT_TYPE_EXECUTIVE_EDUCATION,
    bootcamp:
      (pageType === FIRST_LEVEL_RESULTS && !hasFilters && bootcamp.items.length > 0)
      || (pageType === SECOND_LEVEL_RESULTS && resultType === RESULT_TYPE_BOOT_CAMP),
    degreePrograms:
      (pageType === FIRST_LEVEL_RESULTS && !hasFilters && degree.items.length > 0)
      || (pageType === SECOND_LEVEL_RESULTS && resultType === RESULT_TYPE_DEGREE_PROGRAM),
  };

  emitEventIfAble('edx.bi.user.search.results.viewed', {
    search_string: query,
    search_refinements: JSON.stringify(refinements),
    results_page_type: pageType,
    results_page_number: currentPage,
    displayed_lob_edx_courses: isDisplayed.courses,
    displayed_lob_edx_programs: isDisplayed.programs,
    displayed_lob_executive_education_courses: isDisplayed.execEd,
    displayed_lob_boot_camps: isDisplayed.bootcamp,
    displayed_lob_degree_programs: isDisplayed.degreePrograms,
    all_displayed_offering_uuids: displayedUuids,
  });
};

const addTrackingParamsToUrl = (location, redirectUrl, lobExperimentVariation = '') => {
  const currentUrlParams = new URLSearchParams(location.search);
  const redirectUrlWithUpdatedParams = new URL(redirectUrl);
  const { searchParams: redirectUrlParams } = redirectUrlWithUpdatedParams;

  if (currentUrlParams.has('linked_from')) {
    redirectUrlParams.set('linked_from', currentUrlParams.get('linked_from'));
  }
  if (currentUrlParams.has('search_term')) {
    redirectUrlParams.set('edxSearch', currentUrlParams.get('search_term'));
  }

  Array.from(currentUrlParams.entries()).filter(([_key]) => _key.startsWith('filtered_')).forEach(([_key, _value]) => {
    redirectUrlParams.set(_key, _value);
  });

  if (currentUrlParams.has('placement_url') && typeof window.location !== 'undefined' && window.location) {
    redirectUrlParams.set('placement_url', `${window.location.protocol}//${window.location.host}${window.location.pathname}`);
  }
  if (lobExperimentVariation) {
    redirectUrlParams.set('vanguards_test', 'merchandise_2u_lobs');
    redirectUrlParams.set('vanguards_variation', lobExperimentVariation);

    if (currentUrlParams.has('vanguards_click')) {
      redirectUrlParams.set('vanguards_click', currentUrlParams.get('vanguards_click'));
    }
  }

  return redirectUrlWithUpdatedParams.toString();
};

const getConditionalUrlParams = (urlParams, is2UDegreeProgram) => {
  let referrerUrl;
  let newAddedParams = { ...urlParams } || {};

  if (typeof window !== 'undefined' && window.location) {
    referrerUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  }

  if (is2UDegreeProgram) {
    newAddedParams = {
      ...urlParams,
      category: 'degrees',
      medium: 'referral',
      lsrc: 'edx',
      version: 'edx',
      source: 'edx',
    };
  }

  if (referrerUrl) {
    newAddedParams.placement_url = referrerUrl;
  }

  return newAddedParams;
};

// These properties only live on search results
// They are sent in the product card click event and eventually relayed to optimizely
// We also pass the tracking parameters through the url into the product about pages
// This way, when a user interacts with the page or clicks enroll, we can notify Optimizely
// Optimizely uses this data for data-driven ranking
// The required fields are objectID, position, queryID, and index
// index should already be included on the tracking object that is passed into the card container
const addOptimizelyTracking = (product, trackingObject) => {
  const newTrackingObject = { ...trackingObject };
  if (!newTrackingObject || Object.keys(newTrackingObject) < 1) {
    return newTrackingObject;
  }
  if (product.objectID) {
    newTrackingObject.objectID = product.objectID;
  }
  if (product.position) {
    newTrackingObject.position = product.position;
  }
  if (product.queryID) {
    newTrackingObject.queryID = product.queryID;
  }
  return newTrackingObject;
};

export {
  submitEvent,
  addTrackingToLink,
  submitPageViewAnalytics,
  emitEventIfAble,
  trackSearchResults,
  addTrackingParamsToUrl,
  getConditionalUrlParams,
  addOptimizelyTracking,
};
